// ---------------------------------------------------------------------------
// Configuration resolution, kept pure and free of `import.meta` so it can be
// unit tested. env.ts feeds it the real Vite environment.
// ---------------------------------------------------------------------------

export interface RawEnv {
  VITE_SUPABASE_URL?: string | undefined;
  VITE_SUPABASE_ANON_KEY?: string | undefined;
  VITE_PMTILES_URL?: string | undefined;
  VITE_GLYPHS_URL?: string | undefined;
  [key: string]: unknown;
}

export interface Config {
  supabaseUrl: string;
  supabaseAnonKey: string;
  pmtilesUrl: string;
  glyphsUrl: string;
  problems: string[];
}

/** Assets that ship with the app, used unless something overrides them. */
export const BUNDLED_PMTILES = '/yucaipa.pmtiles';
export const BUNDLED_GLYPHS = '/fonts/{fontstack}/{range}.pbf';

/**
 * Vite inlines an unset-but-present variable (`VITE_FOO=`) as an empty string,
 * not undefined, so `??` does not fall back and the caller silently receives
 * "". That is how the basemap came to render as a blank background: the URL
 * was "", which is falsy enough to take the no-basemap branch but not nullish
 * enough to pick up the default.
 */
function optional(value: string | undefined, fallback: string): string {
  return value === undefined || value.trim() === '' ? fallback : value.trim();
}

export function resolveConfig(raw: RawEnv): Config {
  const problems: string[] = [];

  // PLAN 2.5 / 14: the service_role key bypasses RLS. Vite inlines any
  // VITE_-prefixed variable, so a mis-named secret would ship to every phone.
  for (const key of Object.keys(raw)) {
    if (/SERVICE_ROLE|SECRET|PRIVATE_KEY/i.test(key)) {
      problems.push(
        `${key} is prefixed VITE_ and has been inlined into the client bundle. ` +
          `Rename it so it does not start with VITE_ and rotate the key.`,
      );
    }
  }

  const required = (name: string, value: string | undefined): string => {
    if (!value || value.trim() === '') {
      problems.push(`${name} is not set. Copy .env.example to .env and fill it in.`);
      return '';
    }
    return value.trim();
  };

  return {
    supabaseUrl: required('VITE_SUPABASE_URL', raw.VITE_SUPABASE_URL),
    supabaseAnonKey: required('VITE_SUPABASE_ANON_KEY', raw.VITE_SUPABASE_ANON_KEY),
    pmtilesUrl: optional(raw.VITE_PMTILES_URL, BUNDLED_PMTILES),
    glyphsUrl: optional(raw.VITE_GLYPHS_URL, BUNDLED_GLYPHS),
    problems,
  };
}
